/**
 * Frontal face-region geometry for FaceRegionMask.
 * Landmark indices match MediaPipe Face Mesh.
 */

export const REGION_COLORS = {
  forehead: '#B99BE8',
  cheek: '#91B6E1',
  nose: '#E8A18E',
  chin: '#B99BE8',
};

export const SKIN_REGIONS = {
  forehead: {
    id: 'forehead',
    label: 'Forehead',
    group: 'forehead',
    color: REGION_COLORS.forehead,
    kind: 'forehead',
  },
  leftCheek: {
    id: 'leftCheek',
    label: 'Left Cheek',
    group: 'cheeks',
    color: REGION_COLORS.cheek,
    points: [
      234, 93, 132, 58, 172,
      136, 150, 176,
      212, 202, 147, 123,
      116, 117, 118, 101,
      50, 36, 111,
    ],
  },
  rightCheek: {
    id: 'rightCheek',
    label: 'Right Cheek',
    group: 'cheeks',
    color: REGION_COLORS.cheek,
    points: [
      454, 323, 361, 288, 397,
      365, 379, 400,
      432, 422, 376, 352,
      345, 346, 347, 330,
      280, 266, 340,
    ],
  },
  nose: {
    id: 'nose',
    label: 'Nose',
    group: 'nose',
    color: REGION_COLORS.nose,
    kind: 'nose',
  },
  chin: {
    id: 'chin',
    label: 'Chin',
    group: 'chin',
    color: REGION_COLORS.chin,
    kind: 'chin',
  },
};

export const DRAW_ORDER = [
  'leftCheek',
  'rightCheek',
  'forehead',
  'nose',
  'chin',
];

const SHRINK_FOR = {
  forehead: 1.0,
  leftCheek: 0.84,
  rightCheek: 0.84,
  chin: 1.0,
  nose: 1.0,
};

function pointAt(lm, i, W, H) {
  const p = lm[i];
  if (!p) return null;
  return { x: p.x * W, y: p.y * H, i };
}

function pointsFromIndices(lm, indices, W, H) {
  const seen = new Set();
  const pts = [];
  for (const i of indices) {
    if (seen.has(i)) continue;
    seen.add(i);
    const p = pointAt(lm, i, W, H);
    if (p) pts.push(p);
  }
  return pts;
}

function centroid(points) {
  const n = points.length || 1;
  return {
    x: points.reduce((s, p) => s + p.x, 0) / n,
    y: points.reduce((s, p) => s + p.y, 0) / n,
  };
}

function convexHull(points) {
  const pts = points
    .map((p) => ({ x: p.x, y: p.y }))
    .sort((a, b) => a.x - b.x || a.y - b.y);
  if (pts.length <= 3) return pts;

  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower = [];
  for (const p of pts) {
    while (
      lower.length >= 2
      && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i -= 1) {
    const p = pts[i];
    while (
      upper.length >= 2
      && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

function connectPath(points) {
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i += 1) {
    d += ` L ${points[i].x} ${points[i].y}`;
  }
  return `${d} Z`;
}

function smoothPath(points, tension = 0.14) {
  const pts = points.filter(Boolean);
  const n = pts.length;
  if (n < 3) return connectPath(pts);

  const p = (i) => pts[(i + n) % n];
  let d = `M ${p(0).x} ${p(0).y}`;
  for (let i = 0; i < n; i += 1) {
    const p0 = p(i - 1);
    const p1 = p(i);
    const p2 = p(i + 1);
    const p3 = p(i + 2);
    const c1x = p1.x + (p2.x - p0.x) * tension;
    const c1y = p1.y + (p2.y - p0.y) * tension;
    const c2x = p2.x - (p3.x - p1.x) * tension;
    const c2y = p2.y - (p3.y - p1.y) * tension;
    d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`;
  }
  return `${d} Z`;
}

function shrinkTowardCentroid(points, factor) {
  const c = centroid(points);
  return points.map((p) => ({
    x: c.x + (p.x - c.x) * factor,
    y: c.y + (p.y - c.y) * factor,
  }));
}

function foreheadBand(lm, W, H) {
  const hairIdx = [21, 54, 103, 67, 109, 10, 338, 297, 332, 284, 251];
  const browIdx = [70, 63, 105, 66, 107, 9, 336, 296, 334, 293, 300];

  const hair = pointsFromIndices(lm, hairIdx, W, H).sort((a, b) => a.x - b.x);
  const brows = pointsFromIndices(lm, browIdx, W, H).sort((a, b) => a.x - b.x);
  if (hair.length < 3 || brows.length < 3) return [];

  const hairMinY = Math.min(...hair.map((p) => p.y));
  const browMinY = Math.min(...brows.map((p) => p.y));
  const span = Math.max(24, browMinY - hairMinY);
  const topLift = span * 0.35;
  const botPad = span * 0.06;

  const leftX = Math.min(hair[0].x, brows[0].x);
  const rightX = Math.max(hair[hair.length - 1].x, brows[brows.length - 1].x);
  const sideInset = (rightX - leftX) * 0.02;

  const top = hair.map((p) => ({
    x: Math.min(rightX - sideInset, Math.max(leftX + sideInset, p.x)),
    y: p.y - topLift,
  }));

  const botY = browMinY - botPad;
  const botN = 12;
  const bottom = [];
  for (let i = 0; i <= botN; i += 1) {
    const t = i / botN;
    const x = rightX - sideInset - t * (rightX - leftX - 2 * sideInset);
    const edge = Math.abs(t - 0.5) * 2;
    bottom.push({ x, y: botY - edge * edge * span * 0.03 });
  }

  const tl = top[0];
  const tr = top[top.length - 1];
  const br = bottom[0];
  const bl = bottom[bottom.length - 1];
  const midYL = (tl.y + bl.y) / 2;
  const midYR = (tr.y + br.y) / 2;
  const flare = (rightX - leftX) * 0.02;

  return [
    ...top,
    { x: tr.x + flare, y: midYR },
    ...bottom,
    { x: tl.x - flare, y: midYL },
  ];
}

function chinBand(lm, W, H) {
  const mouthL = pointAt(lm, 61, W, H);
  const mouthR = pointAt(lm, 291, W, H);
  const lipBot = pointAt(lm, 17, W, H);
  const tip = pointAt(lm, 152, W, H);
  const sideL = pointAt(lm, 176, W, H) || pointAt(lm, 148, W, H);
  const sideR = pointAt(lm, 400, W, H) || pointAt(lm, 377, W, H);
  const midL = pointAt(lm, 149, W, H) || sideL;
  const midR = pointAt(lm, 378, W, H) || sideR;

  if (!mouthL || !mouthR || !lipBot || !tip) return [];

  const span = Math.max(24, tip.y - lipBot.y);
  const topY = lipBot.y + span * 0.28;
  const botY = tip.y + span * 0.02;
  const halfMouth = Math.abs(mouthR.x - mouthL.x) * 0.42;

  const tl = { x: tip.x - halfMouth, y: topY };
  const tr = { x: tip.x + halfMouth, y: topY };
  const br = {
    x: tip.x + halfMouth * 0.72,
    y: botY - span * 0.08,
  };
  const bl = {
    x: tip.x - halfMouth * 0.72,
    y: botY - span * 0.08,
  };

  const ml = midL
    ? {
      x: tip.x + Math.max(-halfMouth * 0.95, Math.min(-halfMouth * 0.55, midL.x - tip.x)),
      y: topY + (botY - topY) * 0.55,
    }
    : { x: tip.x - halfMouth * 0.85, y: topY + (botY - topY) * 0.55 };
  const mr = midR
    ? {
      x: tip.x + Math.max(halfMouth * 0.55, Math.min(halfMouth * 0.95, midR.x - tip.x)),
      y: topY + (botY - topY) * 0.55,
    }
    : { x: tip.x + halfMouth * 0.85, y: topY + (botY - topY) * 0.55 };

  return [tl, tr, mr, br, { x: tip.x, y: botY }, bl, ml];
}

function noseBand(lm, W, H) {
  const top = pointAt(lm, 6, W, H) || pointAt(lm, 168, W, H);
  const tip = pointAt(lm, 1, W, H);
  const under = pointAt(lm, 2, W, H);
  const wingL = pointAt(lm, 129, W, H) || pointAt(lm, 48, W, H);
  const wingR = pointAt(lm, 358, W, H) || pointAt(lm, 278, W, H);
  const alaL = pointAt(lm, 98, W, H) || pointAt(lm, 64, W, H);
  const alaR = pointAt(lm, 327, W, H) || pointAt(lm, 294, W, H);

  if (!top || !tip || !alaL || !alaR || !wingL || !wingR) return [];

  const baseY = under?.y ?? tip.y;
  const botY = baseY + Math.max(2, (baseY - tip.y) * 0.2);
  const halfTop = Math.abs(alaL.x - alaR.x) * 0.12;
  const midY = top.y + (Math.min(alaL.y, alaR.y) - top.y) * 0.45;
  const inset = (p, t) => ({ x: tip.x + (p.x - tip.x) * (1 - t), y: p.y });
  const wL = inset(wingL, 0.06);
  const wR = inset(wingR, 0.06);
  const aL = inset(alaL, 0.02);
  const aR = inset(alaR, 0.02);

  return [
    { x: top.x - halfTop, y: top.y },
    { x: top.x + (wL.x - top.x) * 0.5, y: midY },
    wL,
    aL,
    { x: tip.x + (aL.x - tip.x) * 0.4, y: botY },
    { x: tip.x, y: botY },
    { x: tip.x + (aR.x - tip.x) * 0.4, y: botY },
    aR,
    wR,
    { x: top.x + (wR.x - top.x) * 0.5, y: midY },
    { x: top.x + halfTop, y: top.y },
  ];
}

/**
 * Build frontal region path descriptors for SVG overlay.
 * @param {Array<{x:number,y:number}>} landmarks normalized 0–1
 * @param {number} W image width
 * @param {number} H image height
 */
export function buildFrontalFaceRegions(landmarks, W, H) {
  if (!landmarks?.length || !W || !H) return [];

  const regions = [];

  DRAW_ORDER.forEach((id) => {
    const def = SKIN_REGIONS[id];
    if (!def) return;

    let outline;
    if (def.kind === 'nose') outline = noseBand(landmarks, W, H);
    else if (def.kind === 'forehead') outline = foreheadBand(landmarks, W, H);
    else if (def.kind === 'chin') outline = chinBand(landmarks, W, H);
    else {
      const pts = pointsFromIndices(landmarks, def.points || [], W, H);
      if (pts.length < 3) return;
      outline = convexHull(pts);

      if (def.group === 'cheeks') {
        const chinTop = chinBand(landmarks, W, H);
        const chinY = chinTop.length
          ? Math.min(...chinTop.map((p) => p.y))
          : null;
        const lift = Math.max(8, H * 0.01);
        outline = outline.map((p) => {
          let y = p.y - lift;
          if (chinY != null && y > chinY - 12) y = chinY - 12;
          return { x: p.x, y };
        });
      }
    }

    if (!outline || outline.length < 3) return;

    outline = shrinkTowardCentroid(outline, SHRINK_FOR[id] ?? 0.88);
    const useSmooth = def.kind !== 'nose';
    const d = useSmooth ? smoothPath(outline, 0.18) : connectPath(outline);
    const c = centroid(outline);

    regions.push({
      id,
      label: def.label,
      group: def.group,
      color: def.color,
      d,
      cx: c.x,
      cy: c.y,
    });
  });

  return regions;
}
